'use client'

import {EditorFlow, useSmoothPanelResize, useVisualViewport} from '@fitro/overlay-layout'

import React, {useEffect, useId, useRef} from 'react'
import {AnimatePresence, motion, useReducedMotion} from 'framer-motion'
import {isTopmostModal, useDialogFocusTrap, useTopmostModalEscape} from '@fitro/modal-routing'
import {createPortal} from 'react-dom'
import {cn} from '../../../lib/utils'
import {usePortalContainer} from '../../../lib/portal-root'
import {useScrollLock} from '../../../hooks/useScrollLock'
import {useTranslation} from '../../../hooks/useTranslation'
import {
    blurActiveEditableOnTouchPointerOutside,
    consumeTimedClickSuppression,
    isPenPointer,
    setTimedClickSuppression,
    suppressNextCompatibleClick,
} from '../../../lib/pointer-events'
import {CUSTOMER_OVERLAY_LAYERS} from '../../../lib/overlay-layers'

type ModalSize = 'small' | 'medium' | 'large'

interface ModalProps {
    isOpen: boolean
    onClose: () => void
    onAfterClose?: () => void
    size?: ModalSize
    title?: string
    /** Names an untitled dialog by an element in its body, such as a centered result heading. */
    labelledBy?: string
    /** A line under the title, such as the date of a class. */
    description?: string
    children: React.ReactNode
    showCloseButton?: boolean
    closeOnBackdropClick?: boolean
    closeOnEscape?: boolean
    preventScroll?: boolean
    className?: string
    contentClassName?: string
    footer?: React.ReactNode
}

const modalSizes: Record<ModalSize, string> = {
    small: 'max-w-md',
    medium: 'max-w-2xl',
    large: 'max-w-4xl',
}

const EASE_OUT = [0.23, 1, 0.32, 1] as const
const MODAL_TRANSITION = {type: 'tween', duration: 0.18, ease: EASE_OUT} as const
const PEN_TAP_MOVE_TOLERANCE_PX = 8

type PenCloseTap = {
    pointerId: number
    target: EventTarget
    clientX: number
    clientY: number
}

export function Modal({
    isOpen,
    onClose,
    onAfterClose,
    size = 'medium',
    title,
    labelledBy,
    description,
    children,
    showCloseButton = true,
    closeOnBackdropClick = true,
    closeOnEscape = true,
    preventScroll = true,
    className,
    contentClassName,
    footer,
}: ModalProps) {
    const modalRef = useRef<HTMLDivElement>(null)
    const modalRootRef = useRef<HTMLDivElement>(null)
    const {t} = useTranslation('common')
    const titleId = useId()
    const portalContainer = usePortalContainer()
    const suppressNextCloseClickRef = useRef<boolean | null>(null)
    const suppressCloseClickTimeoutRef = useRef<number | null>(null)
    const penCloseTapRef = useRef<PenCloseTap | null>(null)
    const reduceMotion = Boolean(useReducedMotion())
    // The root carries the fade; the panel only scales, so the two opacities do not compound.
    const closedTransform = reduceMotion ? 'scale(1)' : 'scale(0.97)'
    const shouldUseVisualViewport = typeof window !== 'undefined' && Boolean(window.visualViewport)

    useScrollLock({enabled: isOpen && preventScroll})
    useDialogFocusTrap(isOpen, modalRef)

    useTopmostModalEscape(isOpen && closeOnEscape, modalRootRef, onClose)

    useVisualViewport(isOpen)
    useSmoothPanelResize(modalRef, isOpen && Boolean(portalContainer))

    useEffect(() => {
        if (!isOpen) return
        const previousFocus = document.activeElement as HTMLElement | null
        const focusTimeout = window.setTimeout(() => {
            const modal = modalRef.current
            // Initialization must yield to an already focused child or a higher
            // selection layer opened while this modal was animating.
            if (modal && isTopmostModal(modalRootRef.current) && !modal.contains(document.activeElement)) {
                modal.focus()
            }
        }, 100)
        return () => {
            window.clearTimeout(focusTimeout)
            previousFocus?.focus?.()
        }
    }, [isOpen])

    const shouldSuppressCloseClick = () => consumeTimedClickSuppression(
        suppressNextCloseClickRef,
        true,
        suppressCloseClickTimeoutRef
    )

    const handleCloseClick = () => {
        if (!shouldSuppressCloseClick()) onClose()
    }

    const handlePenClosePointerDown = (event: React.PointerEvent<HTMLElement>) => {
        if (!isPenPointer(event) || event.isPrimary === false || event.button !== 0) {
            penCloseTapRef.current = null
            return
        }
        penCloseTapRef.current = {
            pointerId: event.pointerId,
            target: event.currentTarget,
            clientX: event.clientX,
            clientY: event.clientY,
        }
    }

    const handlePenClosePointerUp = (event: React.PointerEvent<HTMLElement>) => {
        const penTap = penCloseTapRef.current
        penCloseTapRef.current = null
        if (!isPenPointer(event) || event.isPrimary === false || !penTap) return
        if (penTap.pointerId !== event.pointerId || penTap.target !== event.currentTarget) return
        if (Math.hypot(event.clientX - penTap.clientX, event.clientY - penTap.clientY) > PEN_TAP_MOVE_TOLERANCE_PX) return

        event.preventDefault()
        event.stopPropagation()
        suppressNextCompatibleClick(event)
        setTimedClickSuppression(suppressNextCloseClickRef, true, suppressCloseClickTimeoutRef)
        onClose()
    }

    const clearPenCloseTap = () => {
        penCloseTapRef.current = null
    }

    const handleBackdropPointerDownCapture = (event: React.PointerEvent<HTMLDivElement>) => {
        if (blurActiveEditableOnTouchPointerOutside(event)) {
            setTimedClickSuppression(suppressNextCloseClickRef, true, suppressCloseClickTimeoutRef)
        }
    }

    const handleBackdropClick = () => {
        if (shouldSuppressCloseClick()) return
        if (closeOnBackdropClick) onClose()
    }

    const handlePanelPointerDownCapture = (event: React.PointerEvent<HTMLDivElement>) => {
        blurActiveEditableOnTouchPointerOutside(event)
    }

    if (typeof window === 'undefined' || !portalContainer) return null

    const overlayStyle = shouldUseVisualViewport ? ({
        top: 'var(--fitro-vv-offset-top, 0px)',
        left: 'var(--fitro-vv-offset-left, 0px)',
        width: 'var(--fitro-vv-width, 100%)',
        height: 'var(--fitro-vv-height, 100%)',
        zIndex: CUSTOMER_OVERLAY_LAYERS.utilityModal,
    } as React.CSSProperties) : {zIndex: CUSTOMER_OVERLAY_LAYERS.utilityModal}

    return createPortal(
        <AnimatePresence onExitComplete={onAfterClose}>
            {isOpen ? (
                <motion.div
                    ref={modalRootRef}
                    data-modal-root
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={MODAL_TRANSITION}
                    className={cn(
                        'fixed flex items-center justify-center p-4',
                        'pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))]',
                        !shouldUseVisualViewport && 'inset-0'
                    )}
                    style={overlayStyle}
                >
                    <div
                        className="absolute inset-0 bg-black/50 dark:bg-[var(--scrim)]"
                        data-utility-dialog-backdrop=""
                        onClick={handleBackdropClick}
                        onPointerDownCapture={handleBackdropPointerDownCapture}
                    />
                    <motion.div
                        ref={modalRef}
                        initial={{transform: closedTransform}}
                        animate={{transform: 'scale(1)'}}
                        exit={{transform: closedTransform}}
                        transition={MODAL_TRANSITION}
                        className={cn(
                            'relative flex max-h-[90%] w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900 dark:ring-1 dark:ring-white/15',
                            modalSizes[size],
                            className
                        )}
                        data-utility-dialog=""
                        data-dialog-size={size}
                        tabIndex={-1}
                        role="dialog"
                        aria-modal="true"
                        aria-label={title}
                        aria-labelledby={title ? titleId : labelledBy}
                        onPointerDownCapture={handlePanelPointerDownCapture}
                    >
                        <EditorFlow header={(title || showCloseButton) ? (
                            // Header, body and footer share one inset, so the title, content and actions line up.
                            <div className="flex min-h-14 shrink-0 items-center justify-between border-b border-gray-200 p-4 sm:px-6 dark:border-gray-700">
                                {title && description ? (
                                    <div className="min-w-0">
                                        <h2 id={titleId} className="text-xl font-semibold leading-7 text-gray-900 dark:text-white">
                                            {title}
                                        </h2>
                                        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{description}</p>
                                    </div>
                                ) : title ? (
                                    <h2 id={titleId} className="text-xl font-semibold leading-7 text-gray-900 dark:text-white">
                                        {title}
                                    </h2>
                                ) : <span />}
                                {showCloseButton ? (
                                    <button
                                        type="button"
                                        onClick={handleCloseClick}
                                        onPointerDown={handlePenClosePointerDown}
                                        onPointerUp={handlePenClosePointerUp}
                                        onPointerCancel={clearPenCloseTap}
                                        onPointerLeave={clearPenCloseTap}
                                        className="-m-2 flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                        aria-label={t('close')}
                                    >
                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                        </svg>
                                    </button>
                                ) : null}
                            </div>
                        ) : null}>
                            <div data-editor-body=""
                                className={cn('p-4 sm:p-6', contentClassName)}
                                data-utility-dialog-content=""
                            >
                                {children}
                            </div>

                            {footer ? (
                                <div data-editor-footer=""
                                    className="border-t border-gray-200 bg-white p-4 sm:px-6 dark:border-gray-700 dark:bg-gray-900"
                                    data-utility-dialog-footer=""
                                >
                                    {footer}
                                </div>
                            ) : null}
                        </EditorFlow>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>,
        portalContainer
    )
}

Modal.displayName = 'Modal'
